/**
 * «Семья» — экран родителя.
 *
 * Здесь живёт всё, что определяет правила игры: кто в семье, на каких
 * условиях работает няня, включены ли геометки, как часто рассчитываемся.
 * Всё, что видно на экране, приходит из /api/me уже отфильтрованным сервером.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { activeMembership, useFamily, useSession } from '@/app/session'
import { FeedbackSheet } from '@/features/feedback/FeedbackSheet'
import { PlacesSheet } from '@/features/settings/PlacesSheet'
import { TilesSheet } from '@/features/settings/TilesSheet'
import { useTrust } from '@/shared/api/trust'
import { useCreateInvite, useUpdateSettings, useUpdateTariff } from '@/shared/api/mutations'
import type { Member } from '@/shared/api/types'
import { formatMoney } from '@/shared/format'
import { shareInviteCard } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Screen } from '@/shared/ui/Screen'
import { Sheet } from '@/shared/ui/Sheet'
import { Field, MoneyInput, Segmented, Toggle } from '@/shared/ui/fields'
import { Avatar, Card, Chip, Divider, Row, SectionTitle } from '@/shared/ui/primitives'
import { EmptyState, ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

const PERIODS = [
  { value: 'week' as const, label: 'Неделя' },
  { value: 'halfmonth' as const, label: '2 раза' },
  { value: 'month' as const, label: 'Месяц' },
]

export function FamilyScreen() {
  const me = useSession((state) => state.me)
  const family = useFamily()
  const membership = useSession(activeMembership)
  const familyId = family?.id ?? 0
  const [tariffFor, setTariffFor] = useState<Member | null>(null)
  const [tilesOpen, setTilesOpen] = useState(false)
  const [placesOpen, setPlacesOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const navigate = useNavigate()
  const trust = useTrust(familyId)

  const settings = useUpdateSettings({
    onError: (error) => toast.error(error.message),
  })
  const invite = useCreateInvite({
    onSuccess: (created) => {
      // Карточка от бота с кнопкой вместо голой ссылки; без inline-режима
      // shareInviteCard сам откатится на обычное «Поделиться».
      shareInviteCard(
        created.token,
        created.link,
        created.role === 'nanny'
          ? 'Приглашаю вас в ЯНяню — наш общий журнал дня ребёнка'
          : 'Присоединяйся к нашей семье в ЯНяне',
      )
    },
    onError: (error) => toast.error(error.message),
  })

  if (!me || !family || !membership) {
    return <EmptyState icon="nav-family" title="Семья не найдена" />
  }

  const parents = family.members.filter((m) => m.role === 'parent' && m.status === 'active')
  const nannies = family.members.filter((m) => m.role === 'nanny' && m.status === 'active')
  const familySettings = family.settings

  return (
    <Screen title={family.title}>
      {settings.error && <ErrorBanner message={settings.error.message} />}

      {/* Няня — центральный элемент экрана: условия и есть предмет договорённости */}
      <div>
        <SectionTitle>Няня</SectionTitle>
        {nannies.length === 0 ? (
          <Card className="text-center">
            <p className="text-text-muted text-note leading-snug">
              Няня ещё не присоединилась. Отправьте ей ссылку — она откроет приложение и
              сразу увидит свой день.
            </p>
            <Button
              className="mt-4"
              block
              loading={invite.isPending}
              onClick={() => invite.mutate({ role: 'nanny' })}
            >
              Пригласить няню
            </Button>
          </Card>
        ) : (
          <Card padded={false} className="px-4">
            {nannies.map((member, index) => (
              <div key={member.user_id}>
                {index > 0 && <Divider />}
                <Row
                  icon={<Avatar name={member.first_name} src={member.photo_url} />}
                  title={`${member.first_name} ${member.last_name ?? ''}`.trim()}
                  subtitle={
                    member.hourly_rate
                      ? `${formatMoney(member.hourly_rate, family.currency)} / час`
                      : 'ставка не задана'
                  }
                  right={<Icon name="action-chevron" size={21} />}
                  onClick={() => setTariffFor(member)}
                />
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* Родители */}
      <div>
        <SectionTitle>Родители</SectionTitle>
        <Card padded={false} className="px-4">
          {parents.map((member, index) => (
            <div key={member.user_id}>
              {index > 0 && <Divider />}
              <Row
                icon={<Avatar name={member.first_name} src={member.photo_url} />}
                title={`${member.first_name} ${member.last_name ?? ''}`.trim()}
                subtitle={member.user_id === me.user.id ? 'это вы' : undefined}
              />
            </div>
          ))}
          <Divider />
          <Row
            icon={
              <span className="bg-surface-muted flex size-10 items-center justify-center rounded-full">
                <Icon name="action-add" size={18} />
              </span>
            }
            title="Пригласить второго родителя"
            subtitle="Права будут такими же, как у вас"
            onClick={() => invite.mutate({ role: 'parent' })}
          />
        </Card>
      </div>

      {/* Настройки */}
      {familySettings && (
        <div>
          <SectionTitle>Настройки</SectionTitle>
          <Card className="space-y-1">
            <Field label="Как часто рассчитываетесь">
              <Segmented
                options={PERIODS}
                value={familySettings.timesheet_period}
                onChange={(value) => settings.mutate({ timesheet_period: value })}
              />
            </Field>

            <Divider className="my-3" />

            <Toggle
              label="Геометки"
              description="Точка фиксируется только когда няня сама нажимает кнопку. Няне нужно будет отдельно дать согласие."
              checked={familySettings.geo_enabled}
              onChange={(next) => settings.mutate({ geo_enabled: next })}
              disabled={settings.isPending}
            />
          </Card>
        </div>
      )}

      {/* Настройка того, как приложение выглядит у няни */}
      <div>
        <SectionTitle>Настройка</SectionTitle>
        <Card padded={false} className="px-4">
          <Row
            icon={
              <SettingIcon>
                <Icon name="settings-tiles" size={18} />
              </SettingIcon>
            }
            title="Плитки событий"
            subtitle="Что няня видит на главном экране"
            right={<Arrow />}
            onClick={() => setTilesOpen(true)}
          />
          <Divider />
          <Row
            icon={
              <SettingIcon>
                <Icon name="geo-place" size={18} />
              </SettingIcon>
            }
            title="Места"
            subtitle="Чтобы геометки подписывались словами"
            right={<Arrow />}
            onClick={() => setPlacesOpen(true)}
          />
        </Card>
      </div>

      {/* Центр доверия — главный антиконфликтный экран */}
      <Card padded={false} className="px-4">
        <Row
          icon={
            <SettingIcon>
              <Icon name="trust" size={18} />
            </SettingIcon>
          }
          title="Доверие"
          subtitle="Кто видит данные, что менялось, что подвисло"
          right={
            trust.data && trust.data.needs_attention > 0 ? (
              <Chip tone="warm">{trust.data.needs_attention}</Chip>
            ) : (
              <Arrow />
            )
          }
          onClick={() => navigate('/trust')}
        />
      </Card>

      {/* Закрытая бета: канал обратной связи должен быть виден, а не спрятан
          в подвале настроек — иначе им никто не воспользуется. */}
      <Card padded={false} className="px-4">
        <Row
          icon={
            <SettingIcon>
              <Icon name="feedback" size={18} />
            </SettingIcon>
          }
          title="Идея или проблема"
          subtitle="Напишите нам — мы читаем всё"
          right={<Arrow />}
          onClick={() => setFeedbackOpen(true)}
        />
      </Card>

      <TariffSheet member={tariffFor} currency={family.currency} onClose={() => setTariffFor(null)} />
      <TilesSheet open={tilesOpen} familyId={familyId} onClose={() => setTilesOpen(false)} />
      <PlacesSheet open={placesOpen} familyId={familyId} onClose={() => setPlacesOpen(false)} />
      <FeedbackSheet open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Screen>
  )
}

function TariffSheet({
  member,
  currency,
  onClose,
}: {
  member: Member | null
  currency: string
  onClose: () => void
}) {
  const [rate, setRate] = useState('')
  const update = useUpdateTariff({
    onSuccess: () => {
      toast.success('Условия обновлены')
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  const open = member !== null
  const initial = member?.hourly_rate ?? ''

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={member ? `Условия · ${member.first_name}` : ''}
      footer={
        <Button
          block
          size="lg"
          loading={update.isPending}
          disabled={!rate}
          onClick={() => member && update.mutate({ userId: member.user_id, hourly_rate: rate })}
        >
          Сохранить
        </Button>
      }
    >
      <Field
        label="Ставка за час"
        hint="Уже завершённые смены не пересчитываются — в них сохранена прежняя ставка."
      >
        <MoneyInput
          value={rate || initial}
          onValueChange={setRate}
          suffix="/ час"
          currency={currency === 'RUB' ? '₽' : currency}
        />
      </Field>
    </Sheet>
  )
}


function SettingIcon({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-hidden
      className="bg-surface-muted flex size-10 items-center justify-center rounded-[var(--radius-button)]"
    >
      {children}
    </span>
  )
}

function Arrow() {
  return <Icon name="action-chevron" size={21} />
}
